import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

export type BadgeSize = 'small' | 'large' | 'xl' | '2xl';
export type BadgeTheme = 'primary' | 'secondary' | 'success' | 'warning' | 'danger';
export type BadgeMode = 'word' | 'letter' | 'dot';
export type BadgeContrast = 'ascent' | 'subtle';

@Component({
  selector: 'mib-badge',
  template: `<ng-content></ng-content>`,
  host: {
    '[class]': 'hostClasses()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MibBadgeComponent {
  readonly size = input<BadgeSize>('small');
  readonly theme = input<BadgeTheme>('primary');
  readonly mode = input<BadgeMode>('letter');
  readonly shape = input<string>();
  readonly contrast = input<BadgeContrast>('ascent');

  readonly effectiveShape = computed(() => this.shape() || 'rounded-full');

  protected readonly hostClasses = computed(() => {
    const classes: string[] = [
      // Base styles
      'inline-flex',
      'items-center',
      'justify-center',
      'font-normal',
    ];

    // Theme colors based on contrast
    const contrast = this.contrast();
    const theme = this.theme();

    if (contrast === 'ascent') {
      const themeClasses: Record<BadgeTheme, { bg: string; text: string }> = {
        primary: { bg: 'bg-mib-primary', text: 'text-white' },
        secondary: { bg: 'bg-gray-700', text: 'text-white' },
        success: { bg: 'bg-success', text: 'text-white' },
        warning: { bg: 'bg-orange-600', text: 'text-white' },
        danger: { bg: 'bg-error', text: 'text-white' },
      };
      classes.push(themeClasses[theme].bg, themeClasses[theme].text);
    } else {
      // subtle contrast
      const subtleThemeClasses: Record<BadgeTheme, { bg: string; text: string }> = {
        primary: { bg: 'bg-indigo-100', text: 'text-mib-primary' },
        secondary: { bg: 'bg-gray-300', text: 'text-gray-700' },
        success: { bg: 'bg-green-50', text: 'text-success' },
        warning: { bg: 'bg-orange-50', text: 'text-orange-600' },
        danger: { bg: 'bg-red-50', text: 'text-error' },
      };
      classes.push(subtleThemeClasses[theme].bg, subtleThemeClasses[theme].text);
    }

    // Size and mode combinations
    const mode = this.mode();
    const size = this.size();

    if (mode === 'word') {
      if (size === 'small') {
        classes.push('px-1.5', 'h-4.5 min-w-4.5', 'text-[10px]');
      } else if (size === 'xl') {
        classes.push('px-2.5', 'h-8 min-w-8', 'text-xl');
      } else if (size === '2xl') {
        classes.push('px-4', 'h-12 min-w-12', 'text-2xl');
      } else {
        // large (default)
        classes.push('px-1.5', 'h-5 min-w-5', 'text-xs');
      }
    } else if (mode === 'letter') {
      if (size === 'small') {
        classes.push('w-4.5', 'h-4.5', 'text-[10px]');
      } else if (size === 'xl') {
        classes.push('w-8', 'h-8', 'text-xl');
      } else if (size === '2xl') {
        classes.push('w-12', 'h-12', 'text-2xl');
      } else {
        // large (default)
        classes.push('w-5', 'h-5', 'text-xs');
      }
    } else if (mode === 'dot') {
      if (size === 'small') {
        classes.push('w-2', 'h-2');
      } else if (size === 'xl') {
        classes.push('w-4', 'h-4');
      } else if (size === '2xl') {
        classes.push('w-5', 'h-5');
      } else {
        // large (default)
        classes.push('w-3', 'h-3');
      }
    }

    // Shape (border-radius)
    classes.push(this.effectiveShape());

    return classes.join(' ');
  });
}
